// Zustand: client UI state. NUNCA guardar credentials aqui.
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type AdminUser = { id: string; email: string; nome: string | null } | null;

type UIState = {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebar: (open: boolean) => void;

  // Filtro UI compartilhado entre páginas (ex: sócios search)
  searchQueries: Record<string, string>;
  setSearchQuery: (key: string, q: string) => void;

  // Toast/banner inline
  banner: { type: 'ok' | 'error' | 'info'; message: string } | null;
  setBanner: (b: UIState['banner']) => void;
  clearBanner: () => void;
};

// Memory-storage fallback pra SSR/test envs sem localStorage funcional
const memoryStorage: Storage = (() => {
  const store: Record<string, string> = {};
  return {
    get length() { return Object.keys(store).length; },
    clear() { for (const k of Object.keys(store)) delete store[k]; },
    getItem: (k) => (k in store ? store[k] : null),
    setItem: (k, v) => { store[k] = v; },
    removeItem: (k) => { delete store[k]; },
    key: (i) => Object.keys(store)[i] ?? null,
  };
})();

function pickStorage(): Storage {
  if (typeof window === 'undefined') return memoryStorage;
  try {
    const ls = window.localStorage;
    if (!ls || typeof ls.setItem !== 'function') return memoryStorage;
    // Probe — alguns ambientes têm Storage mas throw em set
    ls.setItem('__probe', '1');
    ls.removeItem('__probe');
    return ls;
  } catch {
    return memoryStorage;
  }
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebar: (open) => set({ sidebarOpen: open }),
      searchQueries: {},
      setSearchQuery: (key, q) => set((s) => ({ searchQueries: { ...s.searchQueries, [key]: q } })),
      banner: null,
      setBanner: (b) => set({ banner: b }),
      clearBanner: () => set({ banner: null }),
    }),
    {
      name: 'simplifica-admin-ui',
      storage: createJSONStorage(() => pickStorage()),
      partialize: (s) => ({ sidebarOpen: s.sidebarOpen, searchQueries: s.searchQueries }),
    },
  ),
);

// Auth state (read-only, hidratado server-side via __root loader)
type AuthState = {
  user: AdminUser;
  setUser: (user: AdminUser) => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  setUser: (user) => set({ user }),
}));
